import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { Trophy, Medal, Award, Download, Copy } from 'lucide-react';
import Button from '../Button';

interface MatrixData {
  category?: string;
  name?: string;
  value: string;
  percentage?: string;
  stacked_data?: {
    label: string;
    value: number;
    color: string;
  }[];
}

interface Visualization {
  type: 'matrix';
  title: string;
  data: MatrixData[];
}

interface MatrixChartProps {
  visualization: Visualization;
  questionType?: string;
  className?: string;
}

const MatrixChart: React.FC<MatrixChartProps> = ({ visualization, className = '' }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [showTooltip, setShowTooltip] = useState<string | null>(null);

  if (!visualization?.data || visualization.data.length === 0) return null;

  const handleCopy = async () => {
    if (!chartRef.current) return;
    try {
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: '#fff',
        useCORS: true,
        height: chartRef.current.scrollHeight,
        windowHeight: chartRef.current.scrollHeight,
      });
      canvas.toBlob(async (blob) => {
        if (blob) {
          await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        }
      });
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleDownload = async () => {
    if (!chartRef.current) return;
    const canvas = await html2canvas(chartRef.current, {
      backgroundColor: '#fff',
      scale: 2,
      useCORS: true,
      height: chartRef.current.scrollHeight,
      windowHeight: chartRef.current.scrollHeight,
    });
    const link = document.createElement('a');
    link.download = 'matrix-chart.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  // Extract ranking data for matrix display
  const hasRankingData = visualization.data[0]?.stacked_data;

  if (!hasRankingData) {
    // Fallback to simple grid display
    return (
      <div className={`bg-white p-6 rounded-lg border border-gray-200 ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-gray-900">{visualization.title}</h4>
          <div className="flex gap-2 no-print">
            <div className="relative">
              <Button
                size="sm"
                variant="ghost"
                icon={<Download className="h-4 w-4" />}
                onClick={handleDownload}
                title="Download chart as image"
                onMouseEnter={() => setShowTooltip('download')}
                onMouseLeave={() => setShowTooltip(null)}
              >
                {''}
              </Button>
              {showTooltip === 'download' && (
                <div className="absolute right-0 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded z-20 whitespace-nowrap">
                  Download chart as image
                </div>
              )}
            </div>
            <div className="relative">
              <Button
                size="sm"
                variant="ghost"
                icon={<Copy className="h-4 w-4" />}
                onClick={handleCopy}
                title="Copy chart as image"
                onMouseEnter={() => setShowTooltip('copy')}
                onMouseLeave={() => setShowTooltip(null)}
              >
                {''}
              </Button>
              {showTooltip === 'copy' && (
                <div className="absolute right-0 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded z-20 whitespace-nowrap">
                  Copy chart as image
                </div>
              )}
            </div>
            {copied && <span className="text-green-600 text-xs ml-1">Copied!</span>}
          </div>
        </div>

        <div ref={chartRef} className="w-full h-full pb-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {visualization.data.map((item, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg text-center">
                <div className="font-medium text-gray-900 mb-1">{item.category || item.name}</div>
                <div className="text-2xl font-bold text-blue-600">{item.value}</div>
                {item.percentage && <div className="text-sm text-gray-600">{item.percentage}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Get ranking positions (1st, 2nd, 3rd place)
  const rankingPositions = hasRankingData
    ? visualization.data[0].stacked_data!.map((s) => s.label)
    : [];

  // Create matrix data structure
  const matrixData = visualization.data.map((item) => {
    const rankings: Record<string, number> = {};
    item.stacked_data?.forEach((stack) => {
      rankings[stack.label] = stack.value;
    });
    return {
      option: item.category || item.name || '',
      rankings,
      total: parseInt(item.value) || 0,
    };
  });

  const getRankIcon = (position: string) => {
    switch (position) {
      case '1st Place':
        return <Trophy className="h-4 w-4 text-yellow-500" />;
      case '2nd Place':
        return <Medal className="h-4 w-4 text-gray-400" />;
      case '3rd Place':
        return <Award className="h-4 w-4 text-amber-600" />;
      default:
        return null;
    }
  };

  const getRankColor = (position: string, intensity: number) => {
    const alpha = 0.1 + intensity * 0.7;
    switch (position) {
      case '1st Place':
        return `rgba(34, 197, 94, ${alpha})`; // Green
      case '2nd Place':
        return `rgba(59, 130, 246, ${alpha})`; // Blue
      case '3rd Place':
        return `rgba(245, 158, 11, ${alpha})`; // Amber
      default:
        return `rgba(107, 114, 128, ${alpha})`; // Gray
    }
  };

  // Find max value for intensity calculation
  const allValues = matrixData.flatMap((item) =>
    Object.values(item.rankings).filter((val) => typeof val === 'number'),
  );
  const maxValue = Math.max(...allValues);

  return (
    <div className={`bg-white p-6 rounded-lg border border-gray-200 ${className}`}>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">{visualization.title}</h4>
          <p className="text-sm text-gray-600">
            Ranking matrix showing how each option performed across different positions
          </p>
        </div>
        <div className="flex gap-2 no-print">
          <div className="relative">
            <Button
              size="sm"
              variant="ghost"
              icon={<Download className="h-4 w-4" />}
              onClick={handleDownload}
              title="Download chart as image"
              onMouseEnter={() => setShowTooltip('download')}
              onMouseLeave={() => setShowTooltip(null)}
            >
              {''}
            </Button>
            {showTooltip === 'download' && (
              <div className="absolute right-0 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded z-20 whitespace-nowrap">
                Download chart as image
              </div>
            )}
          </div>
          <div className="relative">
            <Button
              size="sm"
              variant="ghost"
              icon={<Copy className="h-4 w-4" />}
              onClick={handleCopy}
              title="Copy chart as image"
              onMouseEnter={() => setShowTooltip('copy')}
              onMouseLeave={() => setShowTooltip(null)}
            >
              {''}
            </Button>
            {showTooltip === 'copy' && (
              <div className="absolute right-0 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded z-20 whitespace-nowrap">
                Copy chart as image
              </div>
            )}
          </div>
          {copied && <span className="text-green-600 text-xs ml-1">Copied!</span>}
        </div>
      </div>

      <div ref={chartRef} className="w-full h-full pb-8">
        {/* Legend */}
        <div className="mb-6 flex flex-wrap gap-4 justify-center">
          {rankingPositions.map((position, index) => (
            <div
              key={index}
              className="flex items-center space-x-2 px-3 py-1 bg-gray-50 rounded-full"
            >
              {getRankIcon(position)}
              <span className="text-sm font-medium text-gray-700">{position}</span>
            </div>
          ))}
        </div>

        {/* Matrix Grid */}
        <div className="overflow-x-auto">
          <div className="min-w-full">
            {/* Header Row */}
            <div
              className="grid gap-2 mb-2"
              style={{ gridTemplateColumns: `200px repeat(${rankingPositions.length}, 1fr) 100px` }}
            >
              <div className="p-3 font-semibold text-gray-900 bg-gray-50 rounded-lg">Option</div>
              {rankingPositions.map((position, index) => (
                <div
                  key={index}
                  className="p-3 font-semibold text-gray-700 bg-gray-50 rounded-lg text-center flex items-center justify-center"
                >
                  {getRankIcon(position)}
                  <span className="ml-1 text-xs">{position.split(' ')[0]}</span>
                </div>
              ))}
              <div className="p-3 font-semibold text-gray-900 bg-gray-50 rounded-lg text-center">
                Total
              </div>
            </div>

            {/* Data Rows */}
            {matrixData.map((item, rowIndex) => (
              <div
                key={rowIndex}
                className="grid gap-2 mb-2"
                style={{
                  gridTemplateColumns: `200px repeat(${rankingPositions.length}, 1fr) 100px`,
                }}
              >
                {/* Option Name */}
                <div className="p-3 font-medium text-gray-900 bg-white border border-gray-200 rounded-lg flex items-center">
                  <span className="truncate">{item.option}</span>
                </div>

                {/* Ranking Values */}
                {rankingPositions.map((position, colIndex) => {
                  const value = item.rankings[position] || 0;
                  const intensity = value / maxValue;

                  return (
                    <div
                      key={colIndex}
                      className="p-3 border border-gray-200 rounded-lg text-center relative group transition-all duration-200 hover:scale-105"
                      style={{
                        backgroundColor: getRankColor(position, intensity),
                      }}
                    >
                      <div className="text-lg font-bold text-gray-900">{value}</div>

                      {/* Hover tooltip */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-20 whitespace-nowrap">
                        {item.option} - {position}: {value} votes
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  );
                })}

                {/* Total */}
                <div className="p-3 font-bold text-gray-900 bg-blue-50 border border-blue-200 rounded-lg text-center">
                  {item.total}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary Statistics */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-sm text-gray-600">Most Popular Overall</div>
              <div className="text-lg font-semibold text-blue-600">
                {
                  matrixData.reduce(
                    (max, item) => (item.total > max.total ? item : max),
                    matrixData[0],
                  )?.option
                }
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-600">Most 1st Place Votes</div>
              <div className="text-lg font-semibold text-green-600">
                {
                  matrixData.reduce(
                    (max, item) =>
                      (item.rankings['1st Place'] || 0) > (max.rankings['1st Place'] || 0)
                        ? item
                        : max,
                    matrixData[0],
                  )?.option
                }
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-600">Total Responses</div>
              <div className="text-lg font-semibold text-gray-900">
                {matrixData.reduce((sum, item) => sum + item.total, 0)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatrixChart;
